import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth
  const [error, setError] = useState("");

  // Auth is now 100% cookie-based (httpOnly + Secure + SameSite=None). We
  // probe /auth/me — if the cookie is valid the server returns the user
  // payload, otherwise 401 → mark unauthenticated.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Ensure we have a CSRF cookie for mutating requests. On a fresh
      // page reload the browser still has the auth cookie but the SPA
      // may have lost the csrf cookie reference; /auth/csrf re-issues it.
      if (!document.cookie.match(/marqolix_csrf=/)) {
        try { await api.get("/auth/csrf"); } catch (_e) { /* non-fatal */ }
      }
    } catch (err) {
      // 401 on initial load before login is expected — don't pollute the
      // console. Only surface other failures (network, 5xx, etc.).
      if (err?.response?.status !== 401) {
        console.warn("auth refresh failed:", err?.response?.status || err?.message);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      // /auth/login sets the httpOnly cookie via Set-Cookie. We pull the
      // user payload from the response body — no token storage required.
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Server-side cookie deletion is what actually signs the user out.
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("logout request failed:", err?.message || err);
    }
    setUser(false);
  }, []);

  // Memoise the context value so consumers don't re-render on each render
  // of the provider (iter14 audit fix).
  const value = useMemo(
    () => ({ user, error, login, logout, refresh }),
    [user, error, login, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
